import type { App } from 'vue';
import { i18n } from '@/i18n';
import { configuredString } from '@/kiosk-core/config/kioskRuntimeConfig';
import type { CaptureMode } from '@/kiosk-core/payment/paymentStatus';
import { reloadWithCacheBust } from '@/services/deploymentVersion';
import { getRuntimeApiKey } from '@/services/urlState';
import {
  addBreadcrumb,
  captureException,
  captureMessage,
  endSentrySession,
  flushSentry,
} from '@/telemetry/sentry';
import {
  buildCancelPaymentOnReaderBody,
  cancelReaderUrl,
} from '@/transport/kioskPaymentApi';

const HEARTBEAT_INTERVAL_MS = 15_000;
const BOOT_RELOAD_DELAY_MS = 5000;
const SENTRY_FLUSH_MS = 1500;

const DEV_FAST_RETRIES_KEY = 'entr_dev_fast_http_retries';
const ACTIVE_INTENT_STORAGE_KEY = 'entr_kiosk.active_payment_intent';
const SENTRY_UNLOAD_FLUSH_MS = 1500;

export interface ActivePaymentIntent {
  paymentIntentId: string;
  readerId: string;
  sessionId: string;
  stage?: 'reader' | 'capture';
  partnerStripeId?: string | null;
  // The beacon asks for the release of a hold with this mode
  // (releasesHoldOnCancel).
  capture?: CaptureMode;
}

export interface UnloadGuardOptions {
  cancelUrl?: string;
  apiKey?: string | null;
  storage?: Storage;
  navigator?: Pick<Navigator, 'sendBeacon'>;
  hasActiveFlow?: () => boolean;
}

export interface UnloadGuards {
  uninstall: () => void;
}

export function installUnloadGuards(
  options: UnloadGuardOptions = {}
): UnloadGuards {
  const storage = options.storage ?? safeSessionStorage();
  const beaconClient = options.navigator ?? window.navigator;
  const cancelUrl = options.cancelUrl ?? cancelReaderUrl();
  const apiKey = resolvePaymentApiKey(options.apiKey);

  const onUnload = (): void => {
    fireUnload();
  };

  const onPageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) return;
    fireUnload();
  };

  function fireUnload(): void {
    sendCancelBeacon(storage, beaconClient, cancelUrl, apiKey);
    if (options.hasActiveFlow?.()) {
      endSentrySession('abnormal');
    }
    void flushSentry(SENTRY_UNLOAD_FLUSH_MS);
  }

  window.addEventListener('beforeunload', onUnload);
  window.addEventListener('pagehide', onPageHide);

  return {
    uninstall: () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onPageHide);
    },
  };
}

function sendCancelBeacon(
  storage: Storage | null,
  beaconClient: Pick<Navigator, 'sendBeacon'>,
  cancelUrl: string,
  apiKey: string | null
): void {
  const intent = readActivePaymentIntent(storage);
  if (!intent) return;
  try {
    const beaconUrl = apiKey ? withApiKeyQuery(cancelUrl, apiKey) : cancelUrl;
    const payload = JSON.stringify(buildCancelPaymentOnReaderBody(intent));
    if (typeof beaconClient.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      beaconClient.sendBeacon(beaconUrl, blob);
    } else {
      // fetch keepalive fallback — beforeunload won't await it.
      void fetch(cancelUrl, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'X-Kiosk-Api-Key': apiKey } : {}),
        },
        body: payload,
      }).catch(() => {});
    }
  } catch {
    // best-effort — page is unloading anyway.
  }
}

function resolvePaymentApiKey(optionApiKey?: string | null): string | null {
  return (
    configuredString(optionApiKey) ??
    configuredString(import.meta.env.VITE_ENTR_PAYMENT_API_KEY) ??
    configuredString(import.meta.env.VITE_KIOSK_API_KEY) ??
    configuredString(getRuntimeApiKey())
  );
}

function withApiKeyQuery(url: string, apiKey: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('api_key', apiKey);
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return parsed.toString();
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}api_key=${encodeURIComponent(apiKey)}`;
  }
}

function readActivePaymentIntent(
  storage: Storage | null
): ActivePaymentIntent | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(ACTIVE_INTENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActivePaymentIntent> | null;
    if (
      !parsed ||
      !parsed.paymentIntentId ||
      !parsed.readerId ||
      !parsed.sessionId
    ) {
      return null;
    }
    const stage = parsed.stage === 'capture' ? 'capture' : 'reader';
    if (stage === 'capture') return null;
    return {
      paymentIntentId: parsed.paymentIntentId,
      readerId: parsed.readerId,
      sessionId: parsed.sessionId,
      stage,
      partnerStripeId:
        typeof parsed.partnerStripeId === 'string' && parsed.partnerStripeId
          ? parsed.partnerStripeId
          : null,
      ...(parsed.capture === 'after_print' || parsed.capture === 'at_payment'
        ? { capture: parsed.capture }
        : {}),
    };
  } catch {
    return null;
  }
}

export interface BootEnv {
  prod: boolean;
  transport?: string;
  apiBaseUrl?: string;
  paymentTransport?: string;
  printTransport?: string;
  paymentEnabled?: string;
  kioskApiKey?: string;
}

export interface BootEnvAuditResult {
  ok: boolean;
  errors: string[];
}

export function readBootEnv(): BootEnv {
  const env = import.meta.env;
  return {
    prod: env.PROD === true,
    transport: env.VITE_ENTR_TRANSPORT,
    apiBaseUrl: env.VITE_ENTR_API_BASE_URL,
    paymentTransport: env.VITE_ENTR_PAYMENT_TRANSPORT,
    printTransport: env.VITE_ENTR_PRINT_TRANSPORT,
    paymentEnabled: env.VITE_ENTR_PAYMENT_ENABLED,
    kioskApiKey: env.VITE_KIOSK_API_KEY,
  };
}

export function auditBootEnv(env: BootEnv): BootEnvAuditResult {
  if (!env.prod) return { ok: true, errors: [] };

  const errors: string[] = [];
  if (env.transport !== 'backend') {
    errors.push('VITE_ENTR_TRANSPORT must be "backend" in production');
  }
  if (env.printTransport !== 'kiosk') {
    errors.push('VITE_ENTR_PRINT_TRANSPORT must be "kiosk" in production');
  }
  if (env.paymentEnabled === 'true' && env.paymentTransport !== 'kiosk') {
    errors.push(
      'VITE_ENTR_PAYMENT_TRANSPORT must be "kiosk" when production payments are enabled'
    );
  }
  return { ok: errors.length === 0, errors };
}

export function installBootHardening(vueApp: App): void {
  window.__entrLoadedAt = new Date();
  clearDevFastRetriesIfProd();
  installVueErrorHandlers(vueApp);
  installWindowErrorHandlers();
  startHeartbeat();
}

export function mountEntrApp(vueApp: App): void {
  const env = readBootEnv();
  const audit = auditBootEnv(env);
  if (!audit.ok) {
    handleMisconfiguredEnv(audit);
    return;
  }

  try {
    vueApp.mount('#app');
  } catch (error) {
    captureException(error, 'vue_mount');
    endSentrySession('abnormal');
    renderBootFallback();
    window.setTimeout(() => {
      void reloadAfterFlush();
    }, BOOT_RELOAD_DELAY_MS);
  }
}

function installVueErrorHandlers(vueApp: App): void {
  const sentryErrorHandler = vueApp.config.errorHandler;
  vueApp.config.errorHandler = (error, instance, info) => {
    addBreadcrumb('entr.boot', 'Vue error handler caught an error', { info });
    if (sentryErrorHandler) {
      sentryErrorHandler(error, instance, info);
    } else {
      captureException(error, 'vue_error_handler');
    }
    endSentrySession('abnormal');
  };

  vueApp.config.warnHandler = (message, _instance, trace) => {
    addBreadcrumb('entr.boot', 'Vue warning', { message, trace });
  };
}

function installWindowErrorHandlers(): void {
  if (window.__entrGlobalHandlersRegistered) return;
  window.__entrGlobalHandlersRegistered = true;

  window.addEventListener('error', (event) => {
    console.error('[Global] Uncaught error:', event.error ?? event.message);
    endSentrySession('abnormal');
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Global] Unhandled promise rejection:', event.reason);
    endSentrySession('abnormal');
  });
}

function startHeartbeat(): void {
  window.__entrHeartbeat = Date.now();
  window.setInterval(() => {
    window.__entrHeartbeat = Date.now();
  }, HEARTBEAT_INTERVAL_MS);
}

function renderBootFallback(): void {
  const root = document.querySelector<HTMLDivElement>('#app');
  if (!root) return;

  const title = translate('fatal.title', 'Kiosk is opnieuw aan het starten');
  const subtitle = translate('fatal.subtitle', 'Een moment geduld.');
  const retry = translate('fatal.reload', 'Opnieuw laden');
  root.innerHTML = `
    <main class="boot-fallback" aria-live="assertive">
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(subtitle)}</p>
      <button type="button" data-entr-reload>${escapeHtml(retry)}</button>
    </main>
  `;
  root.querySelector('[data-entr-reload]')?.addEventListener('click', () => {
    void reloadAfterFlush();
  });
  captureMessage('ENTR boot fallback rendered', 'fatal');
}

function renderMisconfiguredFallback(errors: string[]): void {
  const root = document.querySelector<HTMLDivElement>('#app');
  if (!root) return;

  const title = translate('fatal.misconfiguredTitle', 'Kiosk misconfigured');
  const subtitle = translate(
    'fatal.misconfiguredSubtitle',
    'Ask a staff member for help.'
  );
  root.innerHTML = `
    <main class="boot-fallback boot-fallback--misconfigured" aria-live="assertive">
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(subtitle)}</p>
      <pre style="white-space: pre-wrap; font-size: 18px; opacity: 0.6;">${escapeHtml(errors.join('\n'))}</pre>
    </main>
  `;
}

function handleMisconfiguredEnv(audit: BootEnvAuditResult): void {
  for (const error of audit.errors) {
    console.error('[ENTR boot] env audit failed:', error);
  }
  captureMessage('ENTR boot env audit failed', 'fatal', {
    errors: audit.errors,
  });
  renderMisconfiguredFallback(audit.errors);
}

async function reloadAfterFlush(): Promise<void> {
  await flushSentry(SENTRY_FLUSH_MS);
  reloadWithCacheBust();
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function clearDevFastRetriesIfProd(): void {
  if (!import.meta.env.PROD) return;
  try {
    const previous = window.localStorage.getItem(DEV_FAST_RETRIES_KEY);
    if (previous !== null) {
      window.localStorage.removeItem(DEV_FAST_RETRIES_KEY);
      captureMessage(
        'Cleared entr_dev_fast_http_retries on prod boot',
        'warning',
        { previous }
      );
    }
  } catch {
    // localStorage may be unavailable in private modes — best effort.
  }
}

function translate(key: string, fallback: string): string {
  try {
    const translated = i18n.global.t(key);
    if (translated && translated !== key) return translated;
  } catch {
    // i18n may not be wired when this runs in the very-early boot fallback.
  }
  return fallback;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export const __test = {
  DEV_FAST_RETRIES_KEY,
  ACTIVE_INTENT_STORAGE_KEY,
};
